import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import '../styles/Announcements.css';

export default function AnnouncementBanner({ sx = {} }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const res = await api.get('announcements/');
        const items = Array.isArray(res) ? res : (res?.results || []);
        if (!mounted) return;
        setAnnouncement(items[0] || null);
      } catch {
        if (!mounted) return;
        setAnnouncement(null);
      } finally {
        if (!mounted) return;
        setStatus('ready');
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Loading announcements…</Typography>
      </Box>
    );
  }

  const formatDateTime = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return '';
    }
  };

  const chipsFor = (a) => {
    if (!a) return [];
    const chips = [];
    if (a.pinned) chips.push({ key: 'pinned', label: 'Pinned', variant: 'filled' });
    if (a.audience_students) chips.push({ key: 'students', label: 'Students', variant: 'outlined' });
    if (a.audience_club_members) chips.push({ key: 'club', label: 'Club Members', variant: 'outlined' });
    if (a.audience_admins) chips.push({ key: 'admins', label: 'Admins', variant: 'outlined' });
    return chips;
  };

  if (!announcement) {
    return (
      <Paper
        variant="outlined"
        className="announcement-banner"
        sx={{
          borderRadius: 2,
          p: 1.5,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          ...sx,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" className="an-banner-title">
            Announcements
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            No announcements right now.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" className="an-banner-action" onClick={() => navigate('/announcements')}>
          View Announcements
        </Button>
      </Paper>
    );
  }

  const chips = chipsFor(announcement);
  const createdLabel = formatDateTime(announcement.created_at);

  return (
    <Paper
      variant="outlined"
      className="announcement-banner"
      sx={{
        borderRadius: 2,
        p: 1.5,
        ...sx,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
            {announcement.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.25,
              opacity: 0.9,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {announcement.message}
          </Typography>

          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center' }}
          >
            {chips.map((c) => (
              <Chip key={c.key} size="small" label={c.label} variant={c.variant} />
            ))}
            {createdLabel ? (
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Posted {createdLabel}
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <Button size="small" variant="outlined" className="an-banner-action" onClick={() => navigate('/announcements')}>
            View Announcements
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
